/**
 * VoiceForge studio chrome — house Kokoro / Breeze + TTS filter.
 * Customer brand: VoiceForge / Forge Suite only.
 */

import { memo, useCallback, useEffect, useMemo, useState } from "react";
import {
  PiArrowSquareOutBold,
  PiCircleNotchBold,
  PiDownloadSimpleBold,
  PiGoogleLogoBold,
  PiMicrophoneStageBold,
  PiSignOutBold,
  PiWalletBold,
  PiXBold,
} from "react-icons/pi";

import { BackgroundFx } from "./BackgroundFx";
import { HouseEngineChips } from "./HouseEngineChips";
import { TtsFilterStrip } from "./TtsFilterStrip";
import { consumeBinderQuery, loadEndpoint, saveEndpoint } from "../lib/endpoint";
import { routeTts } from "../lib/generate";
import {
  DEFAULT_HOUSE_TTS_ID,
  DEFAULT_HOUSE_VOICE,
  getHouseTtsEngine,
  resolveHouseTtsId,
  type HouseTtsEngine,
} from "../lib/houseEngines";
import { loadTakes, pushTake, type TakeItem } from "../lib/takesStore";
import { VOICEFORGE_HF_BUCKET } from "../lib/hfBucket";
import {
  clampVoiceFilter,
  DEFAULT_VOICE_FILTER,
  filterToBreezeInstructions,
  filterToSynthParams,
  voiceFilterEmotionSuffix,
  voiceFilterIsDefault,
  type TtsVoiceFilter,
} from "../lib/voiceFilter";
import {
  applyTokensFromHash,
  clearSession,
  fetchMe,
  isLoggedIn,
  startGoogleLoginWeb,
} from "../suite/auth";
import { SUITE_MEMBER_PRICE_USD } from "../suite/access";
import { loadSuiteSettings, SUITE_RELAY_URL } from "../suite/settings";
import type { SuiteUser, WalletSnapshot } from "../suite/types";
import { fetchWallet, startCheckout, subscribeMember } from "../suite/wallet";
import { getStoredThemeId, selectTheme } from "../themes/applyTheme";
import { BG_FX_OPTIONS, getBgFxId, setBgFxId } from "../themes/bgFx";
import { listThemes } from "../themes/registry";
import { listPickerMotions, listPickerThemes, SUITE_SKINS_EVENT } from "../themes/suiteBridge";
import type { ThemeId } from "../themes/types";

import "../styles/forge-suite.css";
import "../styles/forge-chrome.css";

const SUITE_HOME = "https://forgesuiteui.com";
const BOOKFORGE = "https://bookforge.forgesuiteui.app";
const MODEL_KEY = "voiceforge.suiteModel.v1";
const VOICE_KEY = "voiceforge.suiteVoice.v1";
const RAIL_KEY = "voiceforge.railOpen.v1";
const BATCH_KEY = "voiceforge.batchOpen.v1";
const FILTER_KEY = "voiceforge.ttsFilter.v1";

function loadStoredModel(): string {
  try {
    return resolveHouseTtsId(localStorage.getItem(MODEL_KEY));
  } catch {
    return DEFAULT_HOUSE_TTS_ID;
  }
}
function saveStoredModel(id: string) {
  try {
    localStorage.setItem(MODEL_KEY, id);
  } catch {
    /* ignore */
  }
}
function loadStoredVoice(modelId: string): string {
  try {
    const v = localStorage.getItem(VOICE_KEY);
    const engine = getHouseTtsEngine(modelId);
    if (v && engine?.voices.some((x) => x.id === v)) return v;
    return engine?.voices[0]?.id || DEFAULT_HOUSE_VOICE;
  } catch {
    return DEFAULT_HOUSE_VOICE;
  }
}
function saveStoredVoice(id: string) {
  try {
    localStorage.setItem(VOICE_KEY, id);
  } catch {
    /* ignore */
  }
}
function loadRailOpen(): boolean {
  try {
    const v = localStorage.getItem(RAIL_KEY);
    return v == null ? false : v === "1";
  } catch {
    return false;
  }
}
function saveRailOpen(open: boolean) {
  try {
    localStorage.setItem(RAIL_KEY, open ? "1" : "0");
  } catch {
    /* ignore */
  }
}
function loadBatchOpen(): boolean {
  try {
    return localStorage.getItem(BATCH_KEY) === "1";
  } catch {
    return false;
  }
}
function saveBatchOpen(open: boolean) {
  try {
    localStorage.setItem(BATCH_KEY, open ? "1" : "0");
  } catch {
    /* ignore */
  }
}
function loadFilter(): TtsVoiceFilter {
  try {
    const raw = localStorage.getItem(FILTER_KEY);
    if (!raw) return { ...DEFAULT_VOICE_FILTER };
    return clampVoiceFilter(JSON.parse(raw) as Partial<TtsVoiceFilter>);
  } catch {
    return { ...DEFAULT_VOICE_FILTER };
  }
}
function saveFilter(f: TtsVoiceFilter) {
  try {
    localStorage.setItem(FILTER_KEY, JSON.stringify(f));
  } catch {
    /* ignore */
  }
}

export const ForgeChrome = memo(() => {
  const [endpoint, setEndpoint] = useState(() => loadEndpoint());
  const [user, setUser] = useState<SuiteUser | null>(null);
  const [wallet, setWallet] = useState<WalletSnapshot | null>(null);
  const [authBusy, setAuthBusy] = useState(false);
  const [text, setText] = useState(
    "Welcome to VoiceForge — Forge Suite voice studio. Kokoro is Standard. Member unlocks Breeze and the TTS filter.",
  );
  const [modelId, setModelId] = useState(loadStoredModel);
  const [voice, setVoice] = useState(() => loadStoredVoice(loadStoredModel()));
  const [format, setFormat] = useState<"wav" | "mp3">("mp3");
  const [filter, setFilter] = useState<TtsVoiceFilter>(loadFilter);
  const [genBusy, setGenBusy] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [takes, setTakes] = useState<TakeItem[]>(() => loadTakes());
  const [batchText, setBatchText] = useState("");
  const [showSkins, setShowSkins] = useState(false);
  const [themeId, setThemeId] = useState<ThemeId>(() => getStoredThemeId());
  const [fxId, setFxId] = useState(() => getBgFxId());
  const [pickerSkins, setPickerSkins] = useState(() => listPickerThemes(listThemes()));
  const [pickerMotions, setPickerMotions] = useState(() =>
    listPickerMotions(BG_FX_OPTIONS),
  );
  useEffect(() => {
    const sync = () => {
      setPickerSkins(listPickerThemes(listThemes()));
      setPickerMotions(listPickerMotions(BG_FX_OPTIONS));
    };
    window.addEventListener(SUITE_SKINS_EVENT, sync);
    const t = window.setTimeout(sync, 400);
    return () => {
      window.removeEventListener(SUITE_SKINS_EVENT, sync);
      window.clearTimeout(t);
    };
  }, []);
  const [showAbout, setShowAbout] = useState(false);
  const [railOpen, setRailOpen] = useState(loadRailOpen);
  const [batchOpen, setBatchOpen] = useState(loadBatchOpen);
  const [mobileRailOpen, setMobileRailOpen] = useState(false);
  const relayUrl = loadSuiteSettings().forgeApiUrl || SUITE_RELAY_URL;
  const isMember = Boolean(wallet?.subscriptionActive);
  const selectedEngine = useMemo(
    () => getHouseTtsEngine(modelId) || getHouseTtsEngine(DEFAULT_HOUSE_TTS_ID)!,
    [modelId],
  );
  const voiceOpts = selectedEngine.voices;

  const closeOverlays = useCallback(() => {
    setShowSkins(false);
    setShowAbout(false);
    setMobileRailOpen(false);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeOverlays();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeOverlays]);

  const refreshAuth = useCallback(async () => {
    applyTokensFromHash();
    if (!isLoggedIn()) {
      setUser(null);
      setWallet(null);
      return;
    }
    try {
      const me = await fetchMe();
      setUser(me);
      const w = await fetchWallet();
      setWallet(w);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    const bound = consumeBinderQuery();
    const ep = bound || loadEndpoint();
    setEndpoint(ep);
    const engine = (ep?.engineId || "").toLowerCase();
    if (engine.includes("breeze")) {
      setModelId("forge/suite-breeze-tts");
      saveStoredModel("forge/suite-breeze-tts");
    } else if (engine.includes("kokoro") || !engine) {
      const id = resolveHouseTtsId(engine || loadStoredModel());
      setModelId(id);
      saveStoredModel(id);
    }
    void refreshAuth();
  }, [refreshAuth]);

  useEffect(() => {
    if (!voiceOpts.some((v) => v.id === voice) && voiceOpts[0]) {
      setVoice(voiceOpts[0].id);
      saveStoredVoice(voiceOpts[0].id);
    }
  }, [modelId, voice, voiceOpts]);

  const toggleRail = () => {
    setRailOpen((v) => {
      const next = !v;
      saveRailOpen(next);
      return next;
    });
  };

  const toggleBatch = () => {
    setBatchOpen((v) => {
      const next = !v;
      saveBatchOpen(next);
      return next;
    });
  };

  const onPickEngine = (engine: HouseTtsEngine) => {
    setModelId(engine.id);
    saveStoredModel(engine.id);
    const nextVoice = engine.voices[0]?.id || DEFAULT_HOUSE_VOICE;
    setVoice(nextVoice);
    saveStoredVoice(nextVoice);
  };

  const onPickVoice = (id: string) => {
    setVoice(id);
    saveStoredVoice(id);
  };

  const onFilterChange = (partial: Partial<TtsVoiceFilter>) => {
    setFilter((prev) => {
      const next = clampVoiceFilter({ ...prev, ...partial });
      saveFilter(next);
      return next;
    });
  };

  const onFilterReset = () => {
    setFilter({ ...DEFAULT_VOICE_FILTER });
    saveFilter({ ...DEFAULT_VOICE_FILTER });
  };

  const unlockMember = () => {
    void subscribeMember().then((u) => u && (window.location.href = u));
  };

  const synthesizeOne = async (line: string): Promise<TakeItem | null> => {
    const useFilter = isMember && !voiceFilterIsDefault(filter);
    const params = useFilter ? filterToSynthParams(filter) : undefined;
    const result = await routeTts({
      mode: "suite",
      endpoint,
      sessionToken: endpoint?.sessionToken || null,
      request: {
        text: line.trim(),
        refAudioDataUrl: null,
        model: selectedEngine.id,
        voice,
        format,
        language: "en",
        speed: params?.speed,
        emotion: useFilter ? voiceFilterEmotionSuffix(filter) || undefined : undefined,
        instructions: useFilter ? filterToBreezeInstructions(filter) : undefined,
        parameters: params,
      },
    });
    if (!result.ok || !result.audioUrl) {
      throw new Error(result.error || "TTS failed");
    }
    const take = pushTake({
      src: result.audioUrl,
      text: line.trim(),
      backend: `${result.backend} · ${selectedEngine.label}`,
      format: result.format || format,
    });
    return take;
  };

  const onGenerate = async () => {
    setGenError(null);
    setGenBusy(true);
    try {
      if (selectedEngine.memberOnly && !isMember) {
        setGenError(
          `Breeze needs Suite Member ($${SUITE_MEMBER_PRICE_USD}/mo). Use Kokoro, or subscribe.`,
        );
        return;
      }
      const lines =
        batchOpen && batchText.trim().length > 0
          ? batchText
              .split(/\r?\n/)
              .map((l) => l.trim())
              .filter(Boolean)
          : [text.trim()];
      if (!lines.length || !lines[0]) {
        setGenError("Enter text to speak");
        return;
      }
      let lastUrl: string | null = null;
      for (const line of lines) {
        const take = await synthesizeOne(line);
        if (take?.src) lastUrl = take.src;
      }
      setTakes(loadTakes());
      if (lastUrl) setPreviewUrl(lastUrl);
      void refreshAuth();
    } catch (e) {
      setGenError(e instanceof Error ? e.message : String(e));
    } finally {
      setGenBusy(false);
    }
  };

  const downloadTake = (t: TakeItem) => {
    if (!t.src) return;
    const a = document.createElement("a");
    a.href = t.src;
    a.download = `voiceforge_${t.id}.${t.format || "mp3"}`;
    a.click();
  };

  const costHint = `Suite · ${selectedEngine.label} · ~$${selectedEngine.costPer1kCharsUsd.toFixed(3)}/1k chars`;

  const takesList = (
    <div className="vf-takes">
      {takes.length === 0 ? (
        <p className="vf-cost-hint">No takes yet — generate speech to fill this shelf.</p>
      ) : (
        takes.slice(0, 12).map((t) => (
          <div key={t.id} className="vf-take">
            <div className="vf-take-meta">
              <span>
                {t.backend} · {new Date(t.createdAt).toLocaleString()}
              </span>
              <button type="button" className="vf-chip" onClick={() => downloadTake(t)}>
                <PiDownloadSimpleBold /> Download
              </button>
            </div>
            <div className="vf-take-text" title={t.text}>
              {t.text}
            </div>
            {t.src ? (
              <audio className="vf-player" controls src={t.src} preload="none">
                <track kind="captions" />
              </audio>
            ) : (
              <span className="vf-cost-hint">Audio expired from storage</span>
            )}
          </div>
        ))
      )}
    </div>
  );

  const batchBlock = (
    <div className="vf-rail-section">
      <div className="vf-rail-head">
        <strong>Batch</strong>
        <button type="button" className="vf-chip" onClick={toggleBatch}>
          {batchOpen ? "Hide" : "Show"}
        </button>
      </div>
      {batchOpen && (
        <textarea
          className="vf-textarea vf-textarea--batch"
          value={batchText}
          onChange={(e) => setBatchText(e.target.value)}
          placeholder={"Chapter line 1\nChapter line 2\n…"}
          aria-label="Batch lines"
        />
      )}
    </div>
  );

  const railInner = (
    <>
      {batchBlock}
      <div className="vf-rail-section vf-rail-section--takes">
        <div className="vf-rail-head">
          <strong>Recent takes</strong>
          <button
            type="button"
            className="vf-chip"
            onClick={() => {
              setRailOpen(false);
              saveRailOpen(false);
              setMobileRailOpen(false);
            }}
            aria-label="Close takes rail"
          >
            <PiXBold />
          </button>
        </div>
        {takesList}
      </div>
    </>
  );

  return (
    <div className="vf-chrome-root">
      <BackgroundFx />
      <header className="vf-chrome" role="banner">
        <div className="vf-chrome-brand">
          <span className="vf-mark" aria-hidden>
            <PiMicrophoneStageBold />
          </span>
          <div className="vf-brand-text">
            <strong>VoiceForge</strong>
            <span>Forge Suite · voice studio</span>
          </div>
        </div>

        <div className="vf-chrome-actions">
          <button
            type="button"
            className={`vf-chip vf-takes-desktop${railOpen ? " vf-chip--on" : ""}`}
            onClick={toggleRail}
            title="Takes & batch"
          >
            Takes
          </button>
          <button
            type="button"
            className={`vf-chip vf-takes-mobile${mobileRailOpen ? " vf-chip--on" : ""}`}
            onClick={() => setMobileRailOpen((v) => !v)}
            title="Takes & batch"
          >
            Takes
          </button>
          <button
            type="button"
            className={`vf-chip${showSkins ? " vf-chip--on" : ""}`}
            onClick={() => {
              setShowSkins((v) => !v);
              setMobileRailOpen(false);
            }}
          >
            <span className="vf-chip-label-full">Skin / Motion</span>
            <span className="vf-chip-label-short">Skin</span>
          </button>
          <button type="button" className="vf-chip" onClick={() => setShowAbout(true)}>
            About
          </button>
          {wallet != null && isLoggedIn() && (
            <span className="vf-wallet" title="Suite wallet (shared)">
              <PiWalletBold /> ${wallet.balanceUsd.toFixed(2)}
              {wallet.subscriptionActive ? " · Member" : ""}
            </span>
          )}
          {isLoggedIn() && user ? (
            <button
              type="button"
              className="vf-chip"
              onClick={() => {
                clearSession();
                setUser(null);
                setWallet(null);
              }}
              title={user.email}
            >
              <PiSignOutBold /> {user.email.split("@")[0]}
            </button>
          ) : (
            <button
              type="button"
              className="vf-btn-primary"
              disabled={authBusy}
              onClick={() => {
                setAuthBusy(true);
                startGoogleLoginWeb();
              }}
            >
              <PiGoogleLogoBold /> Google
            </button>
          )}
        </div>
      </header>

      <HouseEngineChips
        selectedId={selectedEngine.id}
        onSelect={onPickEngine}
        isMember={isMember}
      />
      <TtsFilterStrip
        filter={filter}
        onChange={onFilterChange}
        onReset={onFilterReset}
        isMember={isMember}
        onUnlock={unlockMember}
      />

      <div className="vf-body">
        <main className="vf-studio">
          <div className="vf-engine-bar">
            <label className="vf-engine-field">
              <span className="vf-label">Voice</span>
              <select
                className="vf-select"
                value={voice}
                onChange={(e) => onPickVoice(e.target.value)}
              >
                {voiceOpts.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.label}
                  </option>
                ))}
              </select>
            </label>
            <div className="vf-engine-meta">
              <span>
                Active: <strong>{selectedEngine.label}</strong>
              </span>
            </div>
          </div>

          <section className="vf-speak-pane" aria-label="Text to speak">
            <div className="vf-label">Text to speak</div>
            <textarea
              className="vf-textarea vf-textarea--speak"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Enter dialogue…"
            />
            <div className="vf-gen-row">
              <label className="vf-label">
                Format{" "}
                <select
                  className="vf-select"
                  value={format}
                  onChange={(e) => setFormat(e.target.value as "wav" | "mp3")}
                >
                  <option value="mp3">mp3</option>
                  <option value="wav">wav</option>
                </select>
              </label>
              <button
                type="button"
                className="vf-btn-primary"
                disabled={
                  genBusy ||
                  (!text.trim() && !(batchOpen && batchText.trim()))
                }
                onClick={() => void onGenerate()}
              >
                {genBusy ? (
                  <>
                    <PiCircleNotchBold className="vf-spin" /> Generating…
                  </>
                ) : (
                  "Generate speech"
                )}
              </button>
              <span className="vf-cost-hint">{costHint}</span>
              {genError && <div className="vf-error">{genError}</div>}
              {selectedEngine.memberOnly && !isMember && genError ? (
                <button type="button" className="vf-chip" onClick={unlockMember}>
                  Become Member
                </button>
              ) : null}
            </div>
            {previewUrl && (
              <audio className="vf-player" controls src={previewUrl} autoPlay>
                <track kind="captions" />
              </audio>
            )}
          </section>
        </main>

        <aside
          className={`vf-rail${railOpen ? "" : " vf-rail--collapsed"}`}
          aria-hidden={!railOpen}
          aria-label="Takes and batch"
        >
          {railOpen ? railInner : null}
        </aside>

        {showSkins && (
          <div
            className="vf-drawer-backdrop"
            role="presentation"
            onClick={() => setShowSkins(false)}
          >
            <div
              className="vf-drawer vf-drawer--skins"
              role="dialog"
              aria-label="Skin and motion"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="vf-drawer-head">
                <strong>Skin / Motion</strong>
                <button
                  type="button"
                  className="vf-chip"
                  onClick={() => setShowSkins(false)}
                  aria-label="Close skins"
                >
                  <PiXBold />
                </button>
              </div>
              <div className="vf-drawer-body">
                <div className="vf-panel-title">Skins</div>
                <div className="vf-skin-row">
                  {pickerSkins.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      className={`vf-skin-chip${themeId === t.id ? " vf-skin-chip--on" : ""}`}
                      title={t.description}
                      onClick={() => {
                        selectTheme(t.id as ThemeId);
                        setThemeId(t.id as ThemeId);
                      }}
                    >
                      <span className="vf-skin-swatches">
                        {t.preview.map((c, i) => (
                          <i key={i} style={{ background: c }} />
                        ))}
                      </span>
                      {t.name}
                    </button>
                  ))}
                </div>
                <div className="vf-panel-title">Motion</div>
                <div className="vf-skin-row">
                  {pickerMotions.map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      className={`vf-skin-chip${fxId === o.id ? " vf-skin-chip--on" : ""}`}
                      title={o.blurb}
                      onClick={() => {
                        setBgFxId(o.id);
                        setFxId(o.id);
                      }}
                    >
                      {o.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {mobileRailOpen && (
          <div
            className="vf-drawer-backdrop"
            role="presentation"
            onClick={() => setMobileRailOpen(false)}
          >
            <div
              className="vf-drawer vf-drawer--rail-mobile"
              role="dialog"
              aria-label="Takes and batch"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="vf-drawer-head">
                <strong>Takes &amp; batch</strong>
                <button
                  type="button"
                  className="vf-chip"
                  onClick={() => setMobileRailOpen(false)}
                  aria-label="Close takes"
                >
                  <PiXBold />
                </button>
              </div>
              <div className="vf-drawer-body">{railInner}</div>
            </div>
          </div>
        )}
      </div>

      {showAbout && (
        <div
          className="vf-modal-backdrop"
          role="dialog"
          aria-label="About VoiceForge"
          onClick={() => setShowAbout(false)}
        >
          <div className="vf-modal" onClick={(e) => e.stopPropagation()}>
            <h2>About VoiceForge</h2>
            <p>
              <strong>VoiceForge</strong> is the Forge Suite voice studio. Standard is Kokoro.
              Premium (Member) unlocks Breeze TTS 2 and the TTS filter.
            </p>
            <p>
              Relay: <code>{relayUrl}</code>
            </p>
            <p>
              Shared Google login and USD wallet. App data:{" "}
              <code>{VOICEFORGE_HF_BUCKET}</code>.
            </p>
            <p style={{ fontSize: 11, opacity: 0.75 }}>
              Clone engine incorporates MIT-licensed open-source components; product brand is
              VoiceForge / Forge Suite. Upstream authors retain their copyrights.
            </p>
            <p>
              Member unlock: ${SUITE_MEMBER_PRICE_USD}/mo ·{" "}
              <a href={SUITE_HOME} target="_blank" rel="noreferrer">
                forgesuiteui.com
              </a>
              {" · "}
              <a href={BOOKFORGE} target="_blank" rel="noreferrer">
                BookForge <PiArrowSquareOutBold />
              </a>
            </p>
            {isLoggedIn() && (
              <div className="vf-wallet-actions">
                <button
                  type="button"
                  className="vf-chip"
                  onClick={() => void startCheckout(10).then((u) => u && (window.location.href = u))}
                >
                  Add $10 credits
                </button>
                <button type="button" className="vf-chip" onClick={unlockMember}>
                  Become Member
                </button>
              </div>
            )}
            <button type="button" className="vf-btn-primary" onClick={() => setShowAbout(false)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

ForgeChrome.displayName = "ForgeChrome";
